import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { formatBlockTime } from "../leadAssignment/availabilityBlocks";
import type { CrmDataProvider } from "../providers/types";
import type { ProjectOfficeHours } from "../types";
import {
  planOfficeHoursSave,
  type OfficeHoursDraft,
  type OfficeHoursPlan,
} from "./guardiasRules";
import {
  GUARDIAS_KEY,
  HELP_CLASS,
  QueryBoundary,
  SectionCard,
  useGuardiasSave,
  type GuardiasSectionProps,
} from "./guardiasShared";

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

const toDrafts = (
  rows: readonly ProjectOfficeHours[],
): Record<number, OfficeHoursDraft> =>
  Object.fromEntries(
    WEEKDAYS.map((weekday) => {
      const row = rows.find((hours) => hours.weekday === weekday);
      return [
        weekday,
        {
          opens: row ? formatBlockTime(row.opens_at) : "",
          closes: row ? formatBlockTime(row.closes_at) : "",
        },
      ];
    }),
  );

/**
 * "Horas de atención de la sala de ventas" per weekday: display data only (it never decides who
 * receives a lead). The Team Leader edits it; the asesora reads it.
 */
export const GuardiasOfficeHours = ({
  projectId,
  canEdit,
}: GuardiasSectionProps) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const hours = useQuery({
    queryKey: [GUARDIAS_KEY, projectId, "hours"],
    queryFn: () => dataProvider.getProjectOfficeHours(projectId),
  });

  return (
    <SectionCard
      title={translate("resources.guardias.hours_title")}
      help={translate("resources.guardias.hours_help")}
    >
      <QueryBoundary query={hours}>
        {(rows) =>
          canEdit ? (
            <OfficeHoursForm projectId={projectId} rows={rows} />
          ) : (
            <ul className="divide-y text-base tabular-nums">
              {WEEKDAYS.map((weekday) => {
                const row = rows.find((item) => item.weekday === weekday);
                return (
                  <li
                    key={weekday}
                    className="flex min-h-11 items-center justify-between gap-3"
                  >
                    <span>
                      {translate(`resources.guardias.weekday.${weekday}`)}
                    </span>
                    <span className={row ? "" : "text-muted-foreground"}>
                      {row
                        ? translate("resources.guardias.guardia_range", {
                            start: formatBlockTime(row.opens_at),
                            end: formatBlockTime(row.closes_at),
                          })
                        : "-"}
                    </span>
                  </li>
                );
              })}
            </ul>
          )
        }
      </QueryBoundary>
    </SectionCard>
  );
};

const OfficeHoursForm = ({
  projectId,
  rows,
}: {
  projectId: GuardiasSectionProps["projectId"];
  rows: ProjectOfficeHours[];
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { saving, run } = useGuardiasSave();
  const [drafts, setDrafts] = useState(() => toDrafts(rows));
  const [problem, setProblem] = useState<Extract<
    OfficeHoursPlan,
    { ok: false }
  > | null>(null);

  const setDraft = (weekday: number, patch: Partial<OfficeHoursDraft>) =>
    setDrafts((current) => ({
      ...current,
      [weekday]: { ...current[weekday], ...patch },
    }));

  const onSave = async () => {
    const plan = planOfficeHoursSave(rows, drafts);
    if (!plan.ok) {
      setProblem(plan);
      return;
    }
    setProblem(null);
    await run(async () => {
      for (const operation of plan.operations) {
        if (operation.kind === "delete") {
          await dataProvider.delete("project_office_hours", {
            id: operation.id,
            previousData: { id: operation.id, project_id: projectId },
          });
        } else if (operation.kind === "update") {
          await dataProvider.update("project_office_hours", {
            id: operation.id,
            data: {
              opens_at: operation.opens_at,
              closes_at: operation.closes_at,
            },
            previousData: { id: operation.id, project_id: projectId },
          });
        } else {
          await dataProvider.create("project_office_hours", {
            data: {
              project_id: projectId,
              weekday: operation.weekday,
              opens_at: operation.opens_at,
              closes_at: operation.closes_at,
            },
          });
        }
      }
    });
  };

  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {WEEKDAYS.map((weekday) => (
          <li
            key={weekday}
            className="grid grid-cols-[1fr_1fr] items-end gap-3 sm:grid-cols-[8rem_1fr_1fr]"
          >
            <span className="col-span-2 text-base font-medium sm:col-span-1 sm:pb-2.5">
              {translate(`resources.guardias.weekday.${weekday}`)}
            </span>
            <div className="space-y-1">
              <Label htmlFor={`hours-open-${weekday}`} className={HELP_CLASS}>
                {translate("resources.guardias.hours_open")}
              </Label>
              <Input
                id={`hours-open-${weekday}`}
                type="time"
                className="h-11 text-base"
                value={drafts[weekday].opens}
                onChange={(e) => setDraft(weekday, { opens: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`hours-close-${weekday}`} className={HELP_CLASS}>
                {translate("resources.guardias.hours_close")}
              </Label>
              <Input
                id={`hours-close-${weekday}`}
                type="time"
                className="h-11 text-base"
                value={drafts[weekday].closes}
                onChange={(e) => setDraft(weekday, { closes: e.target.value })}
              />
            </div>
          </li>
        ))}
      </ul>
      {problem ? (
        <p role="alert" className="text-[15px] text-destructive">
          {translate(
            problem.error === "required"
              ? "resources.guardias.hours_error_required"
              : "resources.guardias.hours_error_order",
            {
              day: translate(`resources.guardias.weekday.${problem.weekday}`),
            },
          )}
        </p>
      ) : null}
      <Button type="button" className="h-11" disabled={saving} onClick={onSave}>
        {translate("resources.guardias.hours_save")}
      </Button>
    </div>
  );
};
